/* lib/api/chatActions.js — действия чата над структурными кадрами: inline-черновики, подготовленные
 * правки, фоновая сборка дерева (F40 W5). Отдельный модуль, потому что их зовёт ChatProvider, а он
 * живёт в оболочке и попадает в главный чанк: импорт фасада lib/api/index.js оттуда утащил бы в
 * стартовую загрузку весь слой «Продукции» (адаптеры ТТК, сырья, меню, версий). Фасад
 * реэкспортирует всё отсюда — прочие потребители импортируют по-прежнему из index.js. */
import * as draftEntities from './draftEntities.js';
import * as pendingChanges from './pendingChanges.js';
import * as ttk from './ttk.js';
import { unwrap } from './unwrap.js';
import { adaptDraftEntity } from '../adapters/chats.js';

// ── Чат: структурные кадры (chat-2) ───────────────────────────────────
// Поверх stateless-эндпойнтов inline-черновиков и подготовленных правок. Все методы
// через unwrap() → бросают err{status,code,detail} по контракту fakeApi (карточки feed
// ловят err.code в catch-ветках, как раньше у fakeApi.recomputeDraft/commitDraft/…).

// recompute: пересчёт derived черновика БЕЗ записи. {computed, warnings}; computed только
// для ttk ({cost_per_portion_rub, food_cost_pct, kcal_per_portion, …}, Decimal-строки →
// Number через адаптер), для card/menu — null. Карточка берёт computed (объект чисел).
export async function recomputeDraft(kind, payload, edited) {
  const data = await unwrap(draftEntities.recomputeDraft(kind, payload, edited));
  // Стрим присылает computed как часть кадра draft_entity — там же его форма; здесь
  // переиспользуем тот же коэрс через adaptDraftEntity({computed}).computed.
  const adapted = adaptDraftEntity({ computed: data && data.computed });
  return { computed: adapted.computed, warnings: (data && data.warnings) || [] };
}

// commit: создать реальную сущность из payload. {committed, kind, id, title, editor_route,
// bound_recipes?} | 400 {error:'duplicate'|'invalid', code, detail} (unwrap → throw{code}).
// Карточка ждёт {url} для «Открыть в редакторе» → отдаём editor_route как url + сам объект.
export async function commitDraft({ messageId, proposalId, kind, payload, chatId, buildTree }) {
  const data = await unwrap(
    draftEntities.commitDraft({ messageId, proposalId, kind, payload, chatId, buildTree }),
  );
  return {
    ...data,
    url: data.editor_route || null,
    bound_recipes: data.bound_recipes || [],
  };
}

// F40 W5: фоновая сборка дерева детей (tree-job; живёт на беке, переживает refresh).
// unwrap → {status, active, total, done, failed, cancel_requested, job_id?,
//           root_entity_kind?, root_entity_id?, nodes:[…]}.
export async function treeBuildStatus(jobId, req) {
  return unwrap(ttk.getTreeBuildStatus(jobId, req));
}
export async function treeBuildByRoot(rootKind, rootId) {
  return unwrap(ttk.getTreeBuildByRoot(rootKind, rootId));
}
export async function treeBuildStart(rootKind, rootId, ctx = {}) {
  return unwrap(ttk.startTreeBuild(rootKind, rootId, ctx));
}
export async function treeBuildCancel(jobId) {
  return unwrap(ttk.cancelTreeBuild(jobId));
}
export async function treeBuildResume(jobId) {
  return unwrap(ttk.resumeTreeBuild(jobId));
}

// commit pending-change: применить подготовленную правку. 200 {committed, summary,
// restore_version_id, restore_version_number, target:{kind,id}, …} | 409 {error:'stale'|
// 'expired'|'gone'} | 404 | 400 {error:'invalid'}. unwrap прокидывает err.error/err.status
// → карточка (catch) различает stale-ветку по err.error/err.status===409.
export async function commitPendingChange(id, body) {
  return unwrap(pendingChanges.commitPendingChange(id, body));
}

// cancel pending-change: 200 {cancelled, status} | 409 {error:'committed'} | 404.
export async function cancelPendingChange(id) {
  return unwrap(pendingChanges.cancelPendingChange(id));
}

// restore: откат применённой правки через versioning-REST (version_number, не PK).
export async function restorePendingChange(kind, targetId, versionNumber) {
  return unwrap(pendingChanges.restoreEntityVersion(kind, targetId, versionNumber));
}
